import React, {useEffect, useState, useRef, useCallback} from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {apiSubs, apiSubsCheck, apiSubsQuantity, apiSubsStatus, apiSubsMail, apiOffers, apiAlert} from 'api-methods'
import {getFromLocalStorage} from 'helper-func'
import Header from '../components/Header'
import BreadCrumbs from '../components/BreadCrumbs'
import FilterPanelSubs from '../components/FilterPanelSubs'
import Footer from '../components/Footer'
import Pagination from '../components/Pagination'
import Notification from "../components/Notification"
import {Loaders} from "../components/Loaders"
import '../css/SubscriptionList.css'
import '../css/SubscriptionListPopup.css'
import '../css/SubscriptionListIcon.css'
import { handleAuthErrorAndRedirect, isSuccessStatus } from '../utils/authError'

const PENDING_QUANTITY_NOTIFICATION_KEY = 'subscriptionsQuantitySaveNotification'

const SubscriptionsList = () => {
    const ITEMS_PER_PAGE = 10
    const [notification, setNotification] = useState(null)
    const showNotification = (message, type = 'success') => {setNotification({ message, type })}
    const hideNotification = () => {setNotification(null)}
    const buttonNamesFull = ['Ключ', 'Лицензий', 'Цена', 'B2B/B2C', 'Период', 'Создана / Истекает', 'Статус',]
    const buttonNames = buttonNamesFull
    const getInitialState = () => {
        const savedState = localStorage.getItem('tableSettings')
        if (savedState) {
            return JSON.parse(savedState)
        }
        const initialState = {}
        buttonNamesFull.forEach(name => {
            initialState[name] = buttonNames.includes(name)
        })
        return initialState
    }
    const [activeButtons, setActiveButtons] = useState(getInitialState)
    const [subscriptions, setSubscriptions] = useState([])
    const [isLdapUser, setIsLdapUser] = useState(false)
    const [isTableLoading, setIsTableLoading] = useState(false)
    const [selectedSubscription, setSelectedSubscription] = useState(null)
    const [showInfoPopup, setShowInfoPopup] = useState(false)
    const [showEditPopup, setShowEditPopup] = useState(false)
    const [showEmailPopup, setShowEmailPopup] = useState(false)
    const [showConfirmPopup, setShowConfirmPopup] = useState(false)
    const [emailInput, setEmailInput] = useState('')
    const [emailError, setEmailError] = useState('')
    const [popupActionError, setPopupActionError] = useState('')
    const [isEmailSubmitting, setIsEmailSubmitting] = useState(false)
    const [isSuspendSubmitting, setIsSuspendSubmitting] = useState(false)
    const [suspendSubmittingAction, setSuspendSubmittingAction] = useState('')
    const [subscriptionActionMode, setSubscriptionActionMode] = useState('')
    const popupRef = useRef(null)
    const [offersCache, setOffersCache] = useState({})
    const [initialPrice, setInitialPrice] = useState(null)
    const [initialQuantity, setInitialQuantity] = useState(1)
    const [editQuantity, setEditQuantity] = useState(1)
    const [minAndMaxOfferLic, setMinAndMaxOfferLic] = useState([0, 0])
    const [isLoadingOffers, setIsLoadingOffers] = useState(false)
    const [pressSpeed, setPressSpeed] = useState(0)
    const pressSpeedRef = useRef(0)
    const isPressingRef = useRef(false)
    const currentDirectionRef = useRef(0)
    const accelerateIntervalRef = useRef(null)
    const editQuantityRef = useRef(1)
    const changeIntervalRef = useRef(null)
    const pressStartTimeRef = useRef(0)
    const lastChangeTimeRef = useRef(0)
    const changeCountRef = useRef(0)
    const pressTimerRef = useRef(null)
    const pressIntervalRef = useRef(null)
    const quantityInputValidationTimerRef = useRef(null)
    const skipNextFetchRef = useRef(false)
    const [isQuantitySubmitting, setIsQuantitySubmitting] = useState(false)

    const navigate = useNavigate()
    const location = useLocation()
    const currentPage = (() => {
        const pageFromUrl = parseInt(new URLSearchParams(location.search).get('page') || '1', 10)
        return Number.isNaN(pageFromUrl) || pageFromUrl < 1 ? 1 : pageFromUrl
    })()

    const buildFilterForApiReq = useCallback((search) => {
        let filterForApiReq = {}
        const urlParams = new URLSearchParams(search)
        const searchType = urlParams.get('searchType')
        const searchValue = urlParams.get('search')
        const status = urlParams.get('status')
        const page = parseInt(urlParams.get('page') || '1', 10)
        const dateRange = urlParams.get('subsDataRange')

        filterForApiReq.page = Number.isNaN(page) || page < 1 ? 1 : page
        filterForApiReq.itemsInPage = ITEMS_PER_PAGE

        if (searchType && searchValue) {
            filterForApiReq[searchType === 'id' ? 'idLike' : searchType] = searchValue
        }

        filterForApiReq.status = status || '1'

        if (dateRange && dateRange.includes('-')) {
            const dates = dateRange.split('-')
            if (dates.length === 2 && dates[0] && dates[1] && dates[0] !== 'пусто' && dates[1] !== 'пусто') {
                filterForApiReq.date_from = dates[0].substr(6, 4) + '-' + dates[0].substr(3, 2) + '-' + dates[0].substr(0, 2)
                filterForApiReq.date_to = dates[1].substr(6, 4) + '-' + dates[1].substr(3, 2) + '-' + dates[1].substr(0, 2)
            }
        }

        return filterForApiReq
    }, [ITEMS_PER_PAGE])

    const parseQuantity = (value) => {
        if (value === '' || value === null || value === undefined) return 0
        return parseInt(String(value).replace(/\D/g, ''), 10) || 0
    }

    const getQuantityLimitsFromProductName = (productName) => {
        if (!productName || typeof productName !== 'string') return null
        const matches = [...productName.matchAll(/(?:^|[^0-9])(\d+)\s*-\s*(\d+)(?=\s*[A-Za-zА-Яа-я])/g)]
        if (matches.length === 0) return null
        const lastMatch = matches[matches.length - 1]
        const min = parseQuantity(lastMatch[1])
        const max = parseQuantity(lastMatch[2])
        return min > 0 && max >= min ? [min, max] : null
    }

    const normalizeSubscriptionsData = useCallback((rawData) => {
        if (Array.isArray(rawData)) {
            return rawData
        }
        if (rawData && typeof rawData === 'object') {
            if (Object.keys(rawData).length === 0) {
                return []
            }
            return [rawData]
        }
        if (typeof rawData === 'string') {
            try {
                const parsed = JSON.parse(rawData)
                if (Array.isArray(parsed)) {
                    return parsed
                }
                if (parsed && typeof parsed === 'object') {
                    if (Object.keys(parsed).length === 0) {
                        return []
                    }
                    return [parsed]
                }
            } catch (e) {
                return []
            }
        }
        return []
    }, [])

    const reportTableLoadError = useCallback((stage, code = 0, extra = {}) => {
        apiAlert({
            apiBlock: 'SubscriptionsList.tableLoad',
            stage,
            ...extra
        }, code || 0).then(_ => {})
    }, [])

    useEffect(() => {
        const pendingNotification = localStorage.getItem(PENDING_QUANTITY_NOTIFICATION_KEY)
        if (!pendingNotification) return

        localStorage.removeItem(PENDING_QUANTITY_NOTIFICATION_KEY)
        try {
            const parsedNotification = JSON.parse(pendingNotification)
            showNotification(parsedNotification.message || 'Данные успешно сохранены!', parsedNotification.type || 'success')
        } catch (e) {
            showNotification('Данные успешно сохранены!', 'success')
        }
    }, [])

    useEffect(() => {
        const loadOffers = async () => {
            setIsLoadingOffers(true)
            try {
                const response = await apiOffers(true)
                if (isSuccessStatus(response.code) && !!response.data && Object.keys(response.data).length > 0) {
                    if (typeof(response.data) === 'string') {
                        response.data = JSON.parse(response.data)
                    }
                    setOffersCache(response.data)
                }
            } catch (error) {
            } finally {
                setIsLoadingOffers(false)
            }
        }
        loadOffers()
    }, [])

    const getProductOffersBySku = useCallback((subs) => {
        let productOffers = []
        if (Object.keys(offersCache).length > 0) {
            Object.keys(offersCache).forEach(item => {
                if (Object.keys(offersCache[item].products).length > 0) {
                    Object.keys(offersCache[item].products).forEach(itemProduct => {
                        if (offersCache[item].products[itemProduct].id === subs.productId) {
                            Object.keys(offersCache[item].products).forEach(itemProductMatch => {
                                productOffers.push({
                                    id: offersCache[item].products[itemProductMatch].id,
                                    min: offersCache[item].products[itemProductMatch].optionsSchema.properties.quantity.minimum,
                                    max: offersCache[item].products[itemProductMatch].optionsSchema.properties.quantity.maximum,
                                    price: offersCache[item].products[itemProductMatch].price,
                                    name: offersCache[item].name
                                })
                            })
                        }
                    })
                }
            })
        }
        const sorted = productOffers.sort((a, b) => a.min - b.min)
        return sorted
    }, [offersCache])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                if (isEmailSubmitting || isSuspendSubmitting) return
                setShowInfoPopup(false)
                setShowEditPopup(false)
                setShowEmailPopup(false)
                setShowConfirmPopup(false)
                setEmailError('')
                setPopupActionError('')
                setInitialQuantity(1)
                setInitialPrice(null)
                setEditQuantity(1)
            }
        }
        if (showInfoPopup || showEditPopup || showEmailPopup || showConfirmPopup) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => {document.removeEventListener('mousedown', handleClickOutside)}
    }, [showInfoPopup, showEditPopup, showEmailPopup, showConfirmPopup, isEmailSubmitting, isSuspendSubmitting])

    useEffect(() => {
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false
            return
        }
        const filterForApiReq = buildFilterForApiReq(location.search)
        setIsLdapUser(getFromLocalStorage('isLdap') === true)
        setIsTableLoading(true)
        apiSubs(filterForApiReq)
            .then(data => {
                apiSubsCheck(data)
                if (handleAuthErrorAndRedirect({statusCode: data.code, navigate, location})) return
                if (data.code === 404) {
                    setSubscriptions([])
                } else if (!isSuccessStatus(data.code)) {
                    reportTableLoadError('loadBySearch:responseError', data.code, {filter: filterForApiReq})
                    setSubscriptions([])
                } else {
                    setSubscriptions(normalizeSubscriptionsData(data.data))
                }
            })
            .catch(error => {
                reportTableLoadError('loadBySearch:catch', 0, {filter: filterForApiReq, error: error?.message || String(error)})
                setSubscriptions([])
            })
            .finally(() => {setIsTableLoading(false)})
    }, [location.search, buildFilterForApiReq, normalizeSubscriptionsData, reportTableLoadError])

    const handlePageChange = (targetPage) => {
        if (isTableLoading || targetPage === currentPage) {
            return
        }
        const nextParams = new URLSearchParams(location.search)
        nextParams.set('page', String(targetPage))
        const nextSearch = `?${nextParams.toString()}`
        const filterForApiReq = buildFilterForApiReq(nextSearch)

        setIsLdapUser(getFromLocalStorage('isLdap') === true)
        setIsTableLoading(true)
        apiSubs(filterForApiReq)
            .then(data => {
                apiSubsCheck(data)
                if (handleAuthErrorAndRedirect({statusCode: data.code, navigate, location})) return
                if (data.code === 404) {
                    setSubscriptions([])
                } else if (!isSuccessStatus(data.code)) {
                    reportTableLoadError('pageChange:responseError', data.code, {filter: filterForApiReq})
                    setSubscriptions([])
                } else {
                    setSubscriptions(normalizeSubscriptionsData(data.data))
                }
                skipNextFetchRef.current = true
                navigate({pathname: location.pathname, search: nextSearch})
            })
            .catch(error => {
                reportTableLoadError('pageChange:catch', 0, {filter: filterForApiReq, error: error?.message || String(error)})
                setSubscriptions([])
            })
            .finally(() => {setIsTableLoading(false)})
    }

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    const getPopupApiErrorMessage = (data, fallbackMessage) => {
        const apiMessage = data?.data?.message || data?.data?.error || (typeof data?.data === 'string' && data.data.trim())
        if (apiMessage === 'Subscription operation is already pending') {
            return 'Операция по подписке уже выполняется'
        }
        if (apiMessage) return apiMessage
        return fallbackMessage
    }

    const getSubscriptionContactEmail = (subscription) => {
        return subscription?.customer?.options?.contacts?.email?.trim() || ''
    }

    const getSubscriptionCompanyName = (subscription) => {
        return (
            subscription?.customer?.options?.company?.name
            || subscription?.customer?.options?.contacts?.name
            || ''
        ).trim()
    }

    const getSubscriptionDatesText = (subscription) => {
        const createdAt = formatDateOnly(subscription?.createdAt) || '-'
        return `${createdAt} / ${getExpiryDate(subscription)}`
    }

    const formatSubscriptionPeriod = (duration) => {
        const days = parseQuantity(duration)
        if (!days) return '-'
        if (days === 365) return '1 год'
        if (days === 730) return '2 года'
        if (days === 1095) return '3 года'
        return `${days} дней`
    }

    const handleSendEmail = (subscription, customEmail = null) => {
        if (isEmailSubmitting) return
        const emailToSend = customEmail || getSubscriptionContactEmail(subscription)
        if (!emailToSend) {setEmailError('Email не может быть пустым');return}
        if (!validateEmail(emailToSend)) {setEmailError('Введите корректный email адрес');return}
        const sendEmailErrorMessage = 'Сервис по отправке писем временно не доступен. Ведутся работы по восстановлению сервиса. Приносим свои извинения'
        setPopupActionError('')
        setIsEmailSubmitting(true)
        let forApiReq = {"subscriptionId": subscription.id, "mail": emailToSend}
        apiSubsMail(forApiReq)
            .then(data => {
                if (handleAuthErrorAndRedirect({statusCode: data.code, navigate, location})) return
                if (isSuccessStatus(data.code)) {
                    showNotification(('Ключ отправлен на e-mail: ' + emailToSend) + '!', 'success')
                    setShowEmailPopup(false)
                    setEmailInput('')
                    setEmailError('')
                    setPopupActionError('')
                } else {
                    setPopupActionError(sendEmailErrorMessage)
                }
            })
            .catch(_ => {setPopupActionError(sendEmailErrorMessage)})
            .finally(() => {setIsEmailSubmitting(false)})
    }

    const handleEmailClick = (subscription) => {
        setSelectedSubscription(subscription)
        setEmailInput(getSubscriptionContactEmail(subscription))
        setEmailError('')
        setPopupActionError('')
        setIsEmailSubmitting(false)
        setShowEmailPopup(true)
    }

    const handleSendEmailFromPopup = () => {
        if (isEmailSubmitting) return
        setPopupActionError('')
        if (!emailInput.trim()) {setEmailError('Email не может быть пустым');return}
        if (!validateEmail(emailInput)) {setEmailError('Введите корректный email адрес');return}
        handleSendEmail(selectedSubscription, emailInput)
    }

    const handleInfo = (subscription) => {
        setSelectedSubscription(subscription)
        setShowInfoPopup(true)
    }

    const isAnnualSubscription = useCallback((subscription) => {
        const duration = parseQuantity(subscription?.options?.duration)
        return duration >= 360
    }, [])

    const handleEdit = (subscription) => {
        setSelectedSubscription(subscription)
        const initialQty = subscription.options?.quantity || 1
        setInitialQuantity(initialQty)
        setEditQuantity(initialQty)
        editQuantityRef.current = initialQty
        setInitialPrice(subscription.finOptions?.realPrice || subscription.finOptions?.pricePerBillingPeriod)
        setShowEditPopup(true)

        const productOffers = getProductOffersBySku(subscription)
        if (productOffers.length > 0) {
            const minValue = Math.min(...productOffers.map(offer => offer.min))
            const maxValue = Math.max(...productOffers.map(offer => offer.max))

            const effectiveMinValue = isAnnualSubscription(subscription) ? Math.max(minValue, initialQty) : minValue

            setMinAndMaxOfferLic([effectiveMinValue, maxValue])
        } else {
            const productNameLimits = getQuantityLimitsFromProductName(subscription.productName)
            const fallbackMin = productNameLimits ? productNameLimits[0] : 1
            const fallbackMax = productNameLimits ? productNameLimits[1] : Math.max(initialQty, 9999)
            setMinAndMaxOfferLic([isAnnualSubscription(subscription) ? initialQty : fallbackMin, fallbackMax])
        }
    }

    const handleSuspendClick = (subscription) => {
        setSelectedSubscription(subscription)
        setPopupActionError('')
        setIsSuspendSubmitting(false)
        setSuspendSubmittingAction('')
        setSubscriptionActionMode('status')
        setShowConfirmPopup(true)
    }

    const handleRenewPauseClick = (subscription) => {
        setSelectedSubscription(subscription)
        setPopupActionError('')
        setIsSuspendSubmitting(false)
        setSuspendSubmittingAction('')
        setSubscriptionActionMode('renewPause')
        setShowConfirmPopup(true)
    }

    const formatDateOnly = (value) => {
        if (!value || typeof value !== 'string') return ''
        return value.split('T')[0]
    }

    const getExpiryDate = (subscription) => {
        if (!subscription) return 'Бессрочно'

        if (subscription.endTime) {
            return formatDateOnly(subscription.endTime)
        }

        if (subscription.autoSuspend === 1 && subscription.chargingNextTime) {
            return formatDateOnly(subscription.chargingNextTime)
        }

        return 'Бессрочно'
    }

    const shouldShowRenewPauseAction = (subscription) => {
        return subscription?.options?.renewAvailable === true
    }

    const shouldShowResumeAction = (subscription) => {
        if (!subscription) return false
        return subscription.status !== 'active' || subscription.autoSuspend === 1
    }

    const getSubscriptionStatusLabel = (subscription) => {
        if (!subscription) return '-'
        if (subscription.autoSuspend === 1 || subscription.status === 'suspend') return 'Приостановлена'
        if (subscription.status === 'active') return 'Активная'
        return 'Не активная'
    }

    const handleSuspendConfirm = (status = null) => {
        if (!selectedSubscription || isSuspendSubmitting) return
        setPopupActionError('')
        setIsSuspendSubmitting(true)
        const actionStatus = status || (shouldShowResumeAction(selectedSubscription) ? 'resume' : 'stop')
        setSuspendSubmittingAction(actionStatus)
        let forApiReq = {"id": selectedSubscription.id, "status": actionStatus}
        apiSubsStatus(forApiReq)
            .then(data => {
                if (handleAuthErrorAndRedirect({statusCode: data.code, navigate, location})) return
                if (isSuccessStatus(data.code)) {
                    if (actionStatus === 'resume') {
                        showNotification(subscriptionActionMode === 'renewPause' ? 'Подписка продлена' : 'Подписка возобновлена', 'success')
                    } else {
                        showNotification(subscriptionActionMode === 'renewPause' ? 'Подписка приостановлена' : 'Подписка остановлена', 'success')
                    }
                    setShowConfirmPopup(false)
                    setPopupActionError('')
                    window.location.reload()
                } else {
                    setPopupActionError(getPopupApiErrorMessage(data, 'Ошибка при сохранении данных'))
                }
            })
            .catch(_ => {setPopupActionError('Ошибка при сохранении данных')})
            .finally(() => {
                setIsSuspendSubmitting(false)
                setSuspendSubmittingAction('')
            })
    }

    const handleSuspendCancel = () => {
        if (isSuspendSubmitting) return
        setShowConfirmPopup(false)
        setPopupActionError('')
        setSuspendSubmittingAction('')
        setSubscriptionActionMode('')
        setSelectedSubscription(null)
    }

    const getQuantityInputMin = useCallback(() => {
        const minOfferLic = parseQuantity(minAndMaxOfferLic[0])
        if (isAnnualSubscription(selectedSubscription)) {
            return Math.max(parseQuantity(initialQuantity), minOfferLic)
        }
        return minOfferLic
    }, [initialQuantity, minAndMaxOfferLic, selectedSubscription, isAnnualSubscription])

    const scheduleManualQuantityValidation = useCallback((quantity) => {
        if (quantityInputValidationTimerRef.current) {
            clearTimeout(quantityInputValidationTimerRef.current)
        }

        quantityInputValidationTimerRef.current = setTimeout(() => {
            const minValue = getQuantityInputMin()
            const currentValue = parseQuantity(editQuantityRef.current)

            if (editQuantityRef.current === '' || currentValue < minValue) {
                setEditQuantity(minValue)
                editQuantityRef.current = minValue
            }

            quantityInputValidationTimerRef.current = null
        }, 2000)
    }, [getQuantityInputMin])

    const handleQuantityChange = useCallback((newQuantity, source = 'manual') => {
        if (!selectedSubscription) {
            return
        }
        let digitsOnly = String(newQuantity).replace(/[^\d]/g, '')
        let maxValue = minAndMaxOfferLic[1]
        if (digitsOnly > minAndMaxOfferLic[1]) {
            digitsOnly = minAndMaxOfferLic[1]
        }
        const minValue = getQuantityInputMin()
        const isBelowMin = digitsOnly !== '' && parseQuantity(digitsOnly) < minValue

        setEditQuantity(digitsOnly)
        editQuantityRef.current = digitsOnly
        if (source === 'manual' || isBelowMin) {
            scheduleManualQuantityValidation(digitsOnly)
        } else if (quantityInputValidationTimerRef.current) {
            clearTimeout(quantityInputValidationTimerRef.current)
            quantityInputValidationTimerRef.current = null
        }
        if (digitsOnly === '') {
            return
        }
        const numValue = parseInt(digitsOnly, 10)
        if (numValue < minValue) {
            return
        }
        if (numValue >= 0 && numValue <= maxValue) {
            const findMatchingOffer = (quantity) => {
                if (!offersCache || Object.keys(offersCache).length === 0 || !selectedSubscription) {
                    return null
                }
                const productOffers = getProductOffersBySku(selectedSubscription)
                const matchingOffer = productOffers.find(offer =>
                    quantity >= offer.min && quantity <= offer.max
                )
                return matchingOffer
            }
            const matchingOffer = findMatchingOffer(numValue)
            if (matchingOffer) {
                setSelectedSubscription(prev => ({
                    ...prev,
                    productId: matchingOffer.id,
                    productName: matchingOffer.name + ' ' + matchingOffer.min + '-' + matchingOffer.max + ' Node'
                }))
            }
        }
    }, [selectedSubscription, minAndMaxOfferLic, offersCache, getProductOffersBySku, getQuantityInputMin, scheduleManualQuantityValidation])

    const isQuantityValid = useCallback(() => {
        if (!selectedSubscription || editQuantity === '') {
            return false
        }
        const numValue = parseInt(String(editQuantity).replace(/[^\d]/g, ''), 10)
        if (isNaN(numValue)) {
            return false
        }

        const minValue = getQuantityInputMin()
        const maxValue = minAndMaxOfferLic[1]

        return numValue >= minValue && numValue <= maxValue
    }, [selectedSubscription, editQuantity, minAndMaxOfferLic, getQuantityInputMin])

    const isQuantityDecreaseDisabled = useCallback(() => {
        if (!selectedSubscription || editQuantity === '') return true
        return parseQuantity(editQuantity) <= getQuantityInputMin()
    }, [selectedSubscription, editQuantity, getQuantityInputMin])

    const isQuantityIncreaseDisabled = useCallback(() => {
        if (!selectedSubscription || editQuantity === '') return true
        return parseQuantity(editQuantity) >= parseQuantity(minAndMaxOfferLic[1])
    }, [selectedSubscription, editQuantity, minAndMaxOfferLic])

    const changeQuantitySubs = () => {
        if (!selectedSubscription || isQuantitySubmitting) return
        setIsQuantitySubmitting(true)
        let forApiReq = {
            "id": selectedSubscription.id,
            "body": {
                "options": {"quantity": editQuantity}
            }
        }
        apiSubsQuantity(forApiReq)
            .then(data => {
                if (handleAuthErrorAndRedirect({statusCode: data.code, navigate, location})) return
                if (!isSuccessStatus(data.code) && data.code !== 204) {
                    showNotification('Ошибка при сохранении данных', 'error')
                    return
                }
                localStorage.setItem(PENDING_QUANTITY_NOTIFICATION_KEY, JSON.stringify({
                    message: 'Данные успешно сохранены!',
                    type: 'success'
                }))
                window.location.reload()
                // reload не всегда мгновенный, поэтому сбрасываем флаг в finally
            })
            .catch(_ => {setSubscriptions([])})
            .finally(() => { setIsQuantitySubmitting(false) })
    }

    const handlePressStart = (direction) => {
        const allowedMin = getQuantityInputMin()
        const maxValue = minAndMaxOfferLic[1]
        const changeValue = () => {
            const currentNum = parseQuantity(editQuantityRef.current)
            const newValue = currentNum + direction
            if (newValue >= allowedMin && newValue <= maxValue) {
                handleQuantityChange(newValue, 'step')
            }
        }
        changeValue()
        pressTimerRef.current = setTimeout(() => {
            const startTime = Date.now()
            pressIntervalRef.current = setInterval(() => {
                const elapsedSeconds = (Date.now() - startTime) / 1000
                let speed = 1
                if (elapsedSeconds >= 3 && elapsedSeconds < 5) speed = 5
                else if (elapsedSeconds >= 5) speed = 25
                for (let i = 0; i < speed; i++) {
                    const currentNum = parseQuantity(editQuantityRef.current)
                    const newValue = currentNum + direction
                    if (newValue >= allowedMin && newValue <= maxValue) {
                        handleQuantityChange(newValue, 'step')
                    } else {
                        handlePressEnd()
                        break
                    }
                }
            }, 100)
        }, 500)
    }

    const handlePressEnd = () => {
        if (pressTimerRef.current) {
            clearTimeout(pressTimerRef.current)
            pressTimerRef.current = null
        }
        if (pressIntervalRef.current) {
            clearInterval(pressIntervalRef.current)
            pressIntervalRef.current = null
        }
    }

    useEffect(() => {
        return () => {
            if (pressTimerRef.current) clearTimeout(pressTimerRef.current)
            if (pressIntervalRef.current) clearInterval(pressIntervalRef.current)
            if (quantityInputValidationTimerRef.current) clearTimeout(quantityInputValidationTimerRef.current)
        }
    }, [])

    const activeColumns = buttonNamesFull.filter(name => activeButtons[name])

    const renderCellContent = (columnName, subscription) => {
        switch (columnName) {
            case 'Лицензий': return subscription.options?.quantity
            case 'ID клиента': return subscription.customer?.id
            case 'Ключ': return <span className="mono-value">{subscription.activation?.code ?? '-'}</span>
            case 'Статус': return subscription.status === 'active'
                ? <span className="status-badge status-badge--active">Активная</span>
                : <span className="status-badge status-badge--inactive">Не активная</span>
            case 'Создана / Истекает': return (
                <div className="subscription-create">
                    <div className="product-name">{formatDateOnly(subscription.createdAt)}</div>
                    <div className="customer-info">{getExpiryDate(subscription)}</div>
                </div>
            )
            case 'Цена': {
                const specialPrice = subscription.finOptions?.specialPrice
                const hasSpecialPrice = isLdapUser
                    && specialPrice !== null
                    && specialPrice !== undefined
                    && specialPrice !== ''
                    && Number.isFinite(Number(specialPrice))
                const price = parseFloat(hasSpecialPrice ? specialPrice : subscription.finOptions?.realPrice)
                return <>{Number.isFinite(price) ? price.toFixed(2) : '-'}&nbsp;{!!subscription.finOptions?.currency ? subscription.finOptions?.currency : ''}</>
            }
            case 'B2B/B2C': return subscription.options?.isB2c ? 'B2C' : 'B2B'
            case 'Период': return formatSubscriptionPeriod(subscription.options?.duration)
            default: return ''
        }
    }

    function showOrHideValue (strValue)  {
        return strValue !== '' ? strValue : '-'
    }

    function formatNumberWithSpaces(num) {
        const str = typeof num === 'string' ? num : num.toString()
        const [integerPart, decimalPart] = str.split('.')
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger
    }

    const getLimitsText = useCallback(() => {
        if (!selectedSubscription) return '';

        if (minAndMaxOfferLic[0] !== minAndMaxOfferLic[1]) {
            return `от ${minAndMaxOfferLic[0]} до ${minAndMaxOfferLic[1]} лицензий`;
        } else {
            return `доступно ${minAndMaxOfferLic[0]} лицензий`;
        }
    }, [selectedSubscription, minAndMaxOfferLic]);

    return (
        <div className="pageSubscription">
            <Header/>
            <BreadCrumbs title={'Подписки'}/>
            <FilterPanelSubs/>
            <div className="pageSubscriptionContent">
                <div className="subscriptions-table-container">
                    {isTableLoading ? (
                        <div className="subscriptions-table-loader">
                            <Loaders />
                        </div>
                    ) : (
                    <table className="subscriptions-table">
                        <thead>
                        <tr>
                            <th>ID подписки / ( Пользователь )</th>
                            <th className="subscription-column-header">Подписка</th>
                            {activeColumns.map((columnName, index) => (
                                <th
                                    key={index}
                                    className={`${columnName === 'Статус' ? 'status-header' : ''} ${columnName === 'Цена' ? 'price-header' : ''} ${columnName === 'Создана / Истекает' ? 'created-expiry-header' : ''} ${columnName === 'B2B/B2C' ? 'b2b-b2c-header' : ''} ${columnName === 'Лицензий' ? 'licenses-header' : ''}`.trim()}
                                >
                                    {columnName}
                                </th>
                            ))}
                            <th className="actions-header">Действия</th>
                        </tr>
                        </thead>
                        <tbody>
                        {Array.isArray(subscriptions) && Object.keys(subscriptions).length > 0 && (subscriptions + '' !== '') && subscriptions.map((subscription, rowIndex) => (
                            <tr key={subscription.id || rowIndex} className="subscription-row">
                                <td data-label="ID подписки">
                                    <div className="subscription-id">
                                        <div className="product-name">{subscription.id}</div>
                                        <div className="customer-info">({subscription.customer?.id})</div>
                                    </div>
                                </td>
                                <td className="subscription-column-cell" data-label="Подписка">
                                    <div className="subscription-info">
                                        <div className="product-name">{subscription.productName}</div>
                                        <div className="customer-info">
                                            {(() => {
                                                const name = getSubscriptionCompanyName(subscription);
                                                const email = getSubscriptionContactEmail(subscription);

                                                if (name && email) return `${name} → ${email}`;
                                                if (name) return name;
                                                if (email) return email;
                                                return null;
                                            })()}
                                        </div>
                                    </div>
                                </td>
                                {activeColumns.map((columnName, colIndex) => (
                                    <td
                                        key={colIndex}
                                        data-label={columnName}
                                        className={`${columnName === 'Цена' ? 'price-cell' : ''} ${columnName === 'Статус' ? 'status-cell' : ''} ${columnName === 'Создана / Истекает' ? 'created-expiry-cell' : ''} ${columnName === 'B2B/B2C' ? 'b2b-b2c-cell' : ''} ${columnName === 'Лицензий' ? 'licenses-cell' : ''}`.trim()}
                                    >
                                        {renderCellContent(columnName, subscription)}
                                    </td>
                                ))}
                                <td className="actions-cell" data-label="Действия">
                                    <div className="actions-content">
                                        {shouldShowRenewPauseAction(subscription)
                                            ? (<button className="action-btn renew-pause-btn" onClick={() => handleRenewPauseClick(subscription)} title="продлить/приостановить" aria-label="продлить/приостановить"/>)
                                            : (
                                                shouldShowResumeAction(subscription)
                                                    ? (<button className="action-btn start-btn" onClick={() => handleSuspendClick(subscription)} title="Возобновить" aria-label="Возобновить"/>)
                                                    : (<button className="action-btn suspend-btn" onClick={() => handleSuspendClick(subscription)} title="Приостановить" aria-label="Приостановить"/>)
                                            )}
                                        {subscription.status === 'active' ? (
                                            <button
                                                className="action-btn edit-btn"
                                                onClick={() => {
                                                    if (subscription.options?.quantityChangeable) {
                                                        handleEdit(subscription)
                                                    }
                                                }}
                                                title={subscription.options?.quantityChangeable ? 'Редактировать' : 'Редактирование недоступно'}
                                                aria-label={subscription.options?.quantityChangeable ? 'Редактировать' : 'Редактирование недоступно'}
                                                disabled={!subscription.options?.quantityChangeable}
                                            />
                                        ) : ''}
                                        {subscription.status === 'active' ? <button className="action-btn email-btn" onClick={() => handleEmailClick(subscription)} title="Отправить на почту" aria-label="Отправить на почту"/> : ''}
                                        <button className="action-btn info-btn" onClick={() => handleInfo(subscription)} title="Информация" aria-label="Информация"/>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {Array.isArray(subscriptions) && (Object.keys(subscriptions).length === 0 || !(subscriptions + '' !== '')) && (
                            <tr className="subscriptions-empty-row">
                                <td className="subscriptions-empty-cell" colSpan="10">записей не найдено</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                    )}
                </div>
                {notification && (<Notification message={notification.message} type={notification.type} onClose={hideNotification} duration={3000}/>)}
                {(showInfoPopup || showEditPopup || showEmailPopup || showConfirmPopup) && selectedSubscription && (
                    <div className="popup-overlay">
                        <div className="popup-content" ref={popupRef}>
                            <div className="popup-header">
                                <h3>
                                    {showInfoPopup && 'Информация о подписке → Просмотр'}
                                    {showEditPopup && 'Информация о подписке → Изменить кол-во лицензий'}
                                    {showEmailPopup && 'Отправка ключа на почту'}
                                    {showConfirmPopup && (subscriptionActionMode === 'renewPause' ? 'Приостановить/продлить подписку' : (shouldShowResumeAction(selectedSubscription) ? 'Возобновить подписку' : 'Приостановить подписку'))}
                                </h3>
                                <button className="close-btn" onClick={() => {
                                    if (isEmailSubmitting || isSuspendSubmitting) return
                                    setShowInfoPopup(false)
                                    setShowEditPopup(false)
                                    setShowEmailPopup(false)
                                    setShowConfirmPopup(false)
                                    setEmailError('')
                                    setPopupActionError('')
                                    setIsEmailSubmitting(false)
                                    setIsSuspendSubmitting(false)
                                    setSuspendSubmittingAction('')
                                    setSubscriptionActionMode('')
                                    setInitialQuantity(1)
                                    setInitialPrice(null)
                                    setEditQuantity(1)
                                    editQuantityRef.current = 1
                                    handlePressEnd()
                                }} disabled={isEmailSubmitting || isSuspendSubmitting}>×</button>
                            </div>
                            <div className="popup-body">
                                {(showInfoPopup || showEditPopup) && (
                                    <div className="info-grid">
                                        <div className="info-row"><span>Название компании:</span><span>{showOrHideValue(selectedSubscription.customer?.options?.company?.name)}</span></div>
                                        <div className="info-row"><span>ИНН (УНП):</span><span>{showOrHideValue(selectedSubscription.customer?.options?.company?.code)}</span></div>
                                        <div className="info-row"><span>Телефон:</span><span>{showOrHideValue(selectedSubscription.customer?.options?.company?.phone ? selectedSubscription.customer?.options?.company?.phone : selectedSubscription.customer?.options?.contacts?.phone)}</span></div>
                                        <div className="info-row"><span>Email:</span><span>{showOrHideValue(selectedSubscription.customer?.options?.company?.email ? selectedSubscription.customer?.options?.company?.email : selectedSubscription.customer?.options?.contacts?.email)}</span></div>
                                        <div className="info-row"><span>Адрес:</span><span>{showOrHideValue(selectedSubscription.customer?.options?.company?.address)}</span></div>
                                        <div className="info-row"><span>Лицо, уполномоченное на заключение договора:</span><span>{showOrHideValue(selectedSubscription.customer?.options?.contacts?.name)}</span></div>
                                        <div className="info-row"><span>ID подписки:</span><span>{selectedSubscription.id}</span></div>
                                        <div className="info-row"><span>SKU:</span><span>{selectedSubscription.vendorCode ? selectedSubscription.vendorCode : '-'}</span></div>
                                        <div className="info-row info-row--product"><span>Продукт:</span><span>{selectedSubscription.productName}</span></div>
                                        <div className="info-row"><span>Код активации:</span><span>{selectedSubscription.activation?.code !== '' ? selectedSubscription.activation?.code : '-'}</span></div>
                                        <div className="info-row"><span>Статус:</span><span>{selectedSubscription.status === 'active' ? 'Активна' : 'Отписана'}</span></div>
                                        <div className="info-row"><span>B2B/B2C:</span><span>{(selectedSubscription.options?.isB2c) ? 'B2C' : 'B2B'}</span></div>
                                        <div className="info-row">
                                            <span>Истекает:</span>
                                            <span className={selectedSubscription.endTime || (selectedSubscription.autoSuspend === 1 && selectedSubscription.chargingNextTime) ? 'expiry-date' : ''}>
                        {getExpiryDate(selectedSubscription)}
                      </span>
                                        </div>
                                        {selectedSubscription.chargingNextTime && (
                                            <div className="info-row"><span>Дата продления:</span><span>{formatDateOnly(selectedSubscription.chargingNextTime)}</span></div>
                                        )}
                                        <div className="info-row full-width">
                      <span>
                        {((selectedSubscription.options?.isPayg && selectedSubscription.options?.duration === 1) || (selectedSubscription.options?.isPayg && selectedSubscription.options?.duration >= 360)) ? <div>Количество x Цена / Период:<br/><br/><br/></div> : ((!selectedSubscription.options?.isPayg && selectedSubscription.options?.duration === 1) ? <div>Цена / Период:<br/><br/><br/></div> : '')}
                          Стоимость подписки без НДС
                      </span>
                                            <span className="popupPriceFive">
                                                {
                                                    (selectedSubscription.options?.isPayg && selectedSubscription.options?.duration === 1)
                                                        ? <div className="popupPriceFive1">
                                                            <div>
                                                                <div><div><b>{selectedSubscription.options?.quantity}x</b></div></div>
                                                                <div>
                                                                    <div>{selectedSubscription.finOptions?.pricePerBillingPeriod}</div>
                                                                    <div>{selectedSubscription.finOptions?.pricePerDay}</div>
                                                                    <div>&nbsp;</div>
                                                                    <div>{formatNumberWithSpaces(parseFloat(selectedSubscription.finOptions?.pricePerBillingPeriod * selectedSubscription.options?.quantity).toFixed(2))}</div>
                                                                    
                                                                </div>
                                                                <div>
                                                                    <div>{selectedSubscription.finOptions?.currency} / {selectedSubscription.options?.duration >= 360 ? 'год' : 'месяц'}</div>
                                                                    <div>{selectedSubscription.finOptions?.currency} / день</div>
                                                                    <div>&nbsp;</div>
                                                                    <div>{selectedSubscription.finOptions?.currency} / месяц</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        : ''
                                                }
                                                {
                                                    (selectedSubscription.options?.isPayg && selectedSubscription.options?.duration >= 360)
                                                        ? <div className="popupPriceFive2">
                                                            <div><b>{selectedSubscription.options?.quantity} x</b> {formatNumberWithSpaces(parseFloat(selectedSubscription.finOptions?.pricePerBillingPeriod).toFixed(2))} {selectedSubscription.finOptions?.currency} / {(selectedSubscription.options?.duration > 900 ? '3 года' : selectedSubscription.options?.duration > 540 ? '2 года' : 'год')}</div><br/>
                                                            <br/>
                                                            <div>{formatNumberWithSpaces(selectedSubscription.finOptions?.fullPrice)} {selectedSubscription.finOptions?.currency} / {(selectedSubscription.options?.duration > 900 ? '3 года' : selectedSubscription.options?.duration > 540 ? '2 года' : 'год')}</div>
                                                        </div>
                                                        : ''
                                                }
                                                {
                                                    (!selectedSubscription.options?.isPayg && selectedSubscription.options?.duration === 1)
                                                        ? <div className="popupPriceFive3">
                                                            <div>{selectedSubscription.finOptions?.pricePerBillingPeriod} {selectedSubscription.finOptions?.currency} / день</div><br/>
                                                            <br/>
                                                            <div>{formatNumberWithSpaces(selectedSubscription.finOptions?.fullPrice)} {selectedSubscription.finOptions?.currency} / месяц</div>
                                                        </div>
                                                        : ''
                                                }
                                                {
                                                    (!selectedSubscription.options?.isPayg && selectedSubscription.options?.duration > 1 && selectedSubscription.options?.duration < 360)
                                                        ? <div className="popupPriceFive4">
                                                            <div>{formatNumberWithSpaces(selectedSubscription.finOptions?.fullPrice)} {selectedSubscription.finOptions?.currency} / месяц</div>
                                                        </div>
                                                        : ''
                                                }
                                                {
                                                    (!selectedSubscription.options?.isPayg && selectedSubscription.options?.duration >= 360)
                                                        ? <div className="popupPriceFive5">
                                                            <div>{formatNumberWithSpaces(selectedSubscription.finOptions?.fullPrice)} {selectedSubscription.finOptions?.currency} / {(selectedSubscription.options?.duration > 900 ? '3 года' : selectedSubscription.options?.duration > 540 ? '2 года' : 'год')}</div>
                                                        </div>
                                                        : ''
                                                }
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {(selectedSubscription.options?.quantityChangeable && showEditPopup) && (
                                    <div className="info-grid">
                                        <div className="edit-section full-width">
                                            <div className="current-licenses-row">
                                                <span className="current-label">Лицензий сейчас: </span>
                                                <span className="current-value"><b>{initialQuantity}</b> </span>
                                            </div>
                                            <div className="change-licenses-row">
                                                <label htmlFor="subsNewQuantity" className="change-label" />
                                                <div className="quantity-controls">
                                                    <button
                                                        type="button"
                                                        className="quantity-btn"
                                                        disabled={isQuantityDecreaseDisabled()}
                                                        onMouseDown={() => handlePressStart(-1)}
                                                        onMouseUp={handlePressEnd}
                                                        onMouseLeave={handlePressEnd}
                                                        onMouseOut={handlePressEnd}>-</button>
                                                    <input
                                                        id="subsNewQuantity"
                                                        type="text"
                                                        className="quantity-input"
                                                        value={editQuantity}
                                                        onChange={(e) => {handleQuantityChange(e.target.value)}}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'ArrowUp') {
                                                                const currentNum = parseQuantity(editQuantity)
                                                                handleQuantityChange(currentNum + 1, 'step')
                                                            } else if (e.key === 'ArrowDown') {
                                                                const currentNum = parseQuantity(editQuantity)
                                                                handleQuantityChange(currentNum - 1, 'step')
                                                            }
                                                        }}/>
                                                    <button
                                                        type="button"
                                                        className="quantity-btn"
                                                        disabled={isQuantityIncreaseDisabled()}
                                                        onMouseDown={() => handlePressStart(1)}
                                                        onMouseUp={handlePressEnd}
                                                        onMouseLeave={handlePressEnd}
                                                        onMouseOut={handlePressEnd}>+</button>
                                                </div>
                                            </div>
                                            <div className="limits-info">
                                                {isLoadingOffers ? (
                                                    <span className="limits-loading">загрузка лимитов...</span>
                                                ) : offersCache && Object.keys(offersCache).length > 0 ? (
                                                    <span>{getLimitsText()}</span>
                                                ) : (
                                                    <span className="limits-loading">лимиты не найдены</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {showEmailPopup && (
                                    <div className="email-popup-section">
                                        <div className="info-row"><span>Подписка:</span><span>{selectedSubscription.productName}</span></div><br/>
                                        <div className="info-row"><span>ID подписки:</span><span>{selectedSubscription.id}</span></div><br/>
                                        <div className="info-row"><span>Название компании:</span><span>{showOrHideValue(getSubscriptionCompanyName(selectedSubscription))}</span></div><br/>
                                        <div className="info-row"><span>Создана / Истекает:</span><span>{getSubscriptionDatesText(selectedSubscription)}</span></div><br/>
                                        <div className="email-input-section">
                                            <label htmlFor="emailInput"><b>Email для отправки:</b></label><br/>&nbsp;
                                            <input id="emailInput" type="email" className={`email-input ${emailError ? 'email-input-error' : ''}`} value={emailInput} onChange={(e) => {setEmailInput(e.target.value); if (emailError) setEmailError(''); if (popupActionError) setPopupActionError('')}} placeholder="Введите email адрес" disabled={isEmailSubmitting}/>
                                            {emailError && <div className="email-error-message">{emailError}</div>}
                                        </div>
                                    </div>
                                )}
                                {showConfirmPopup && (
                                    <div className="confirm-popup-section">
                                        <div className="confirm-message">
                                            {subscriptionActionMode === 'renewPause'
                                                ? 'Выберите действие с подпиской'
                                                : `Вы уверены, что хотите ${shouldShowResumeAction(selectedSubscription) ? 'возобновить' : 'приостановить'} подписку?`}
                                        </div><br/>
                                        <div className="confirm-details">
                                            <div className="info-row"><span>Подписка:</span><span>{selectedSubscription.productName}</span></div><br/>
                                            <div className="info-row"><span>ID подписки:</span><span>{selectedSubscription.id}</span></div><br/>
                                            <div className="info-row"><span>Название компании:</span><span>{showOrHideValue(getSubscriptionCompanyName(selectedSubscription))}</span></div><br/>
                                            <div className="info-row"><span>Создана / Истекает:</span><span>{getSubscriptionDatesText(selectedSubscription)}</span></div><br/>
                                            <div className="info-row"><span>Текущий статус:</span><span>{getSubscriptionStatusLabel(selectedSubscription)}</span></div>
                                        </div>
                                    </div>
                                )}
                                {popupActionError && (
                                    <div className="popup-action-error">{popupActionError}</div>
                                )}
                            </div>
                            <div className="popup-footer">
                                {showEditPopup && (
                                    <div className="confirm-buttons">
                                        <div className={`edit-button ${(!isQuantityValid() || isQuantitySubmitting) ? 'disabled' : ''}`} onClick={(!isQuantityValid() || isQuantitySubmitting) ? undefined : changeQuantitySubs}>
                                            <span>
                                                {((selectedSubscription.options?.isPayg && selectedSubscription.options?.duration === 1) || (selectedSubscription.options?.isPayg && selectedSubscription.options?.duration >= 360)) ? 'Ок' : 'Изменить'}
                                            </span>
                                            {isQuantitySubmitting && <span className="button-inline-loader" aria-hidden="true"/>}
                                        </div>
                                    </div>
                                )}
                                {showEmailPopup && (
                                    <div className="confirm-buttons">
                                        <div className={`send-email-button ${isEmailSubmitting ? 'disabled' : ''}`} onClick={isEmailSubmitting ? undefined : handleSendEmailFromPopup}>
                                            <span>Отправить на почту</span>{isEmailSubmitting && <span className="button-inline-loader" aria-hidden="true"/>}
                                        </div>
                                    </div>
                                )}
                                {showConfirmPopup && (
                                    <div className="confirm-buttons confirm-buttons--subscription-action">
                                        {subscriptionActionMode === 'renewPause' ? (
                                            <>
                                                <div className={`confirm-button confirm-no ${isSuspendSubmitting ? 'disabled' : ''}`} onClick={isSuspendSubmitting ? undefined : handleSuspendCancel}>Отмена</div>
                                                <div className={`confirm-button confirm-yes ${isSuspendSubmitting ? 'disabled' : ''}`} onClick={isSuspendSubmitting ? undefined : () => handleSuspendConfirm('resume')}>
                                                    <span>Продлить</span>{suspendSubmittingAction === 'resume' && <span className="button-inline-loader" aria-hidden="true"/>}
                                                </div>
                                                <div className={`confirm-button confirm-yes ${isSuspendSubmitting ? 'disabled' : ''}`} onClick={isSuspendSubmitting ? undefined : () => handleSuspendConfirm('stop')}>
                                                    <span>Приостановить</span>{suspendSubmittingAction === 'stop' && <span className="button-inline-loader" aria-hidden="true"/>}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className={`confirm-button confirm-no ${isSuspendSubmitting ? 'disabled' : ''}`} onClick={isSuspendSubmitting ? undefined : handleSuspendCancel}>Нет</div>
                                                <div className={`confirm-button confirm-yes ${isSuspendSubmitting ? 'disabled' : ''}`} onClick={isSuspendSubmitting ? undefined : () => handleSuspendConfirm()}>
                                                    <span>Да</span>{isSuspendSubmitting && <span className="button-inline-loader" aria-hidden="true"/>}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="pagePaginatorContent">
                {(!isTableLoading && Object.keys(subscriptions).length > 0 && subscriptions[0] && subscriptions[0].customData && subscriptions[0].customData.rowTotal && subscriptions[0].customData.rowTotal > 1) ? <Pagination totalPages={Math.ceil(subscriptions[0].customData?.rowTotal / ITEMS_PER_PAGE)} currentPage={currentPage} onPageChange={handlePageChange} disabled={isTableLoading}/> : ''}
            </div>
            <br/>
            <Footer/>
        </div>
    )
}

export default SubscriptionsList
